<template>
  <base-page-layout ref="base-page" :title="alteracaoTitle" :hide-breadcrumbs="$getFirstValue()" class="alteracao-campanha">
    <v-container class="pa-0" fluid>
      <v-row>
        <v-col>
          <title-description
            :title.sync="campaign.title"
            :description.sync="campaign.description"
            :title-error="$v.campaign.title.$error"
            :title-error-message="errorTitle"
            :description-error="$v.campaign.description.$error"
            :description-error-message="errorDescription"
            :status="campaign.status"
            :disable-title="isIndustryCampaign"
            :disable-description="isIndustryCampaign"
            :is-industry-campaign="isIndustryCampaign"
            :tooltip-message="tooltipIndustryMessage"
          ></title-description>
        </v-col>
      </v-row>

      <v-row>
        <v-col v-if="isOfferSchedulePermissionActive" cols="3" class="d-flex">
          <offer-schedule-card
            :offer-schedule="offerSchedule"
            :disabled="isIndustryCampaign"
            :is-industry-campaign="isIndustryCampaign"
            :tooltip-message="tooltipIndustryMessage"
          />
        </v-col>

        <v-col :cols="isOfferSchedulePermissionActive ? 9 : 12" class="d-flex flex-column">
          <products-configuration
            v-if="!$getFirstValue()"
            :products.sync="campaign.products"
            class="mb-6 fill-height"
            :is-industry-campaign="isIndustryCampaign"
            :tooltip-message="tooltipIndustryMessage"
          />

          <segments-configuration
            class="fill-height"
            :segments.sync="campaign.segments"
            :error="getSafe(() => $v.campaign.segments.$error)"
            :is-offer-schedule-personalized="isOfferSchedulePersonalized"
            :disabled="isIndustryCampaign"
            :is-industry-campaign="isIndustryCampaign"
            :tooltip-message="tooltipIndustryMessage"
          />
        </v-col>
      </v-row>

      <v-row>
        <v-col>
          <sms-configuration
            v-model="campaign.message.text"
            :error="getSafe(() => $v.campaign.message.text.$error)"
            :template-id.sync="campaign.template_id"
            :disabled="isIndustryCampaign"
            :is-industry-campaign="isIndustryCampaign"
            :tooltip-message="tooltipIndustryMessage"
          />
        </v-col>
      </v-row>

      <v-row>
        <v-col cols="12">
          <measuring-and-recurrency
            v-model="campaign"
            :validations="$v"
            :disabled="isIndustryCampaign"
            :is-industry-campaign="isIndustryCampaign"
            :tooltip-message="tooltipIndustryMessage"
          />
        </v-col>
      </v-row>
      <v-row>
        <v-col cols="6">
          <cbm-card
            class="card-email-metricas"
            title="Custo estimado"
            :number-text="estimatedCostMessage"
            :percent-description="estimatedCostUnderText"
            :tooltip="estimatedCostHelpText"
            :boolean-loader="loadingEstimatedCost"
          />
        </v-col>
        <v-col cols="6">
          <cbm-card title="Saldo atual" :number-text="`${localeString(credits)} créditos`" :boolean-loader="$apollo.queries.credits.loading" />
        </v-col>
      </v-row>

      <mf-action-buttons :primary-button="primaryButton" :secondary-button="secondaryButton" :extra-button="deleteButton" :cancel-button="cancelButton" />
    </v-container>
  </base-page-layout>
</template>

<script>
import BasePageLayout from '@/components/BasePageLayout.vue'
import { minLength, required, minValue } from 'vuelidate/lib/validators'
import { notOnlySpace, isFuture, timeIsFuture } from '@/helpers/validators.js'
import {
  QUERY_GET_SEGMENTS_ESTIMATES,
  QUERY_GET_CREDITS,
  MUTATION_UPSERT_SMS_CAMPAIGN,
  MUTATION_UPSERT_DRAFT_SMS_CAMPAIGN,
} from '@/modules/campanhas/campanhas-sms/graphql'
import { actionButtons } from '../../../views/mixins/action-buttons.js'

import Swal from 'sweetalert2'
import { deepDelete } from '@/helpers/deepDelete'
import { cloneDeep } from 'lodash'
import { mapGetters, mapActions } from 'vuex'

export default {
  components: {
    BasePageLayout,
    OfferScheduleCard: () => import('@/modules/campanhas/views/components/edit/OfferScheduleCard.vue'),
    ProductsConfiguration: () => import('@/modules/campanhas/views/components/edit/ProductsConfiguration.vue'),
    SegmentsConfiguration: () => import('@/modules/campanhas/views/components/edit/SegmentsConfiguration.vue'),
    SmsConfiguration: () => import('@/modules/campanhas/campanhas-sms/views/components/SmsConfiguration.vue'),
    TitleDescription: () => import('@/modules/campanhas/views/components/edit/TitleDescription.vue'),
    CbmCard: () => import('@/components/CbmCard.vue'),
    MeasuringAndRecurrency: () => import('@/modules/campanhas/views/components/edit/MeasuringAndRecurrency.vue'),
  },

  mixins: [actionButtons],

  props: {
    campaign: Object,
    offerSchedule: {
      type: Object,
      default: () => ({}),
    },
    isOfferSchedulePermissionActive: {
      type: Boolean,
      default: false,
    },
    isOfferSchedulePersonalized: {
      type: Boolean,
      default: false,
    },
  },

  data: () => ({
    credits: 0,
    loadingEstimatedCost: false,
    estimatedCost: 0,
    savingDraft: false,
    redirectPage: 'sms',
  }),

  computed: {
    ...mapGetters({ quickAction: 'getQuickAction' }),

    ...mapGetters(['smsCostMessage']),

    errorTitle() {
      if (!this.$v.campaign.title) return ''
      if (this.$v.campaign.title.$error) {
        if (!this.$v.campaign.title.required) return 'Dê um nome para esta campanha.'
        else if (!this.$v.campaign.title.minLength) return 'O título possui menos de 2 caracteres.'
        else if (!this.$v.campaign.title.maxLength) return 'O título excedeu 70 caracteres.'
        else if (!this.$v.campaign.title.notOnlySpace) return 'Não é permitido salvar apenas espaços.'
      }
      return ''
    },
    errorDescription() {
      if (!this.$v.campaign.description) return ''
      if (this.$v.campaign.description.$error) {
        if (!this.$v.campaign.description.required) return 'Informe detalhes para melhor identificação da campanha.'
        else if (!this.$v.campaign.description.minLength) return 'A descrição possui menos de 2 caracteres.'
        else if (!this.$v.campaign.description.maxLength) return 'A descrição excedeu 150 caracteres.'
        else if (!this.$v.campaign.description.notOnlySpace) return 'Não é permitido salvar apenas espaços.'
      }
      return ''
    },
    alteracaoTitle() {
      if (this.newCampaign) return this.$t('campanhas.nova_campanha')
      else return this.$t('campanhas.editar_campanha')
    },
    newCampaign() {
      let path = this.$router.currentRoute.fullPath
      return path.includes('nova') && true
    },

    estimatedCostMessage() {
      return this.isIndustryCampaign ? '-' : `${this.localeString(this.estimatedCost * this.smsCostMessage)} créditos`
    },

    estimatedCostUnderText() {
      return this.isIndustryCampaign ? '' : 'A campanha só será enviada se o saldo disponível for maior ou igual ao custo da campanha.'
    },

    estimatedCostHelpText() {
      return this.isIndustryCampaign
        ? 'Campanha de SMS custeada pela indústria'
        : 'O custo estimado em créditos é baseado na quantidade de telefones nas segmentações selecionadas'
    },

    isIndustryCampaign() {
      return Boolean(this.campaign.industry)
    },
  },
  apollo: {
    credits: {
      query: QUERY_GET_CREDITS,
      notifyOnNetworkStatusChange: true,
      fetchPolicy: 'no-cache',
      update({ balance }) {
        return balance || 0
      },
    },
  },
  watch: {
    async 'campaign.segments'(value) {
      if (!value?.length) {
        this.estimatedCost = 0
        return
      }
      this.loadingEstimatedCost = true
      try {
        await this.getEstimatedCost(value)
      } catch {
      } finally {
        this.loadingEstimatedCost = false
      }
    },
  },
  created() {
    this.getEstimatedCost(this.campaign.segments)
    if (this.campaign.products) {
      this.campaign.products = this.campaign.products.filter((product) => Boolean(product))
    }
  },
  mounted() {
    this.prebuildAutoCampaign()
  },
  methods: {
    ...mapActions(['unsetQuickAction']),
    async getEstimatedCost(segments) {
      if (!segments.length) {
        this.estimatedCost = 0
        return
      }
      this.loadingEstimatedCost = true
      try {
        const result = await this.$apollo.query({
          query: QUERY_GET_SEGMENTS_ESTIMATES,
          variables: {
            segments_ids: segments.map(({ _id }) => _id),
          },
        })
        this.estimatedCost = result?.data?.getSegmentsEstimates?.sms?.cost || 0
      } catch {
        this.$snackbar({ message: 'Ocorreu um erro ao calcular as estimativas de custo' })
      } finally {
        this.loadingEstimatedCost = false
      }
    },
    async save(isDraft) {
      this.savingDraft = isDraft
      this.$v.$reset()
      this.$v.$touch()

      if (this.$v.$error) {
        this.$snackbar({ message: 'Preencha todos os campos corretamente', snackbarColor: 'error' })
        this.$nextTick(() => {
          this.$vuetify.goTo(document.querySelector('.error--text'), { offset: 200 })
        })
        return
      }

      const campaign = cloneDeep(this.campaign)
      deepDelete(campaign, ['__typename', 'status', 'industry'])
      campaign.segments = campaign.segments.map(({ _id }) => _id)
      campaign.products = campaign.products.map(({ sku }) => sku)

      const mutation = isDraft ? MUTATION_UPSERT_DRAFT_SMS_CAMPAIGN : MUTATION_UPSERT_SMS_CAMPAIGN

      await this.$apollo.mutate({
        mutation: mutation,
        variables: {
          data: campaign,
        },
        context: {
          uri: this.$microservicesUrls['campaigns'],
        },
      })

      Swal.fire({
        text: 'Sua campanha salva',
        icon: 'success',
        showConfirmButton: false,
        timer: 1000,
        customClass: {
          content: 'content-class',
        },
      })

      this.$router.push('/campanhas/sms').catch(() => {})
    },

    async prebuildAutoCampaign() {
      if (!this.getIsQuickAction() || !this.quickAction?.segmentation) {
        return
      }

      this.campaign.segments.push(this.quickAction.segmentation.segment)
      this.campaign.title = this.quickAction.segmentation.title
      this.campaign.description = this.quickAction.segmentation.description

      this.unsetQuickAction()
    },
  },
  validations() {
    if (this.savingDraft) {
      return {
        campaign: {
          title: { required },
          description: { required },
        },
      }
    }
    return {
      campaign: {
        title: { required },
        description: { required },
        segments: {
          required,
          minLength: minLength(1),
        },
        frequency: {
          interval: {
            minValue: minValue(1),
          },
        },
        products: [],
        initial_date: {
          required,
        },
        final_date: {
          required,
        },
        message: {
          text: { required, notOnlySpace },
          send_date: {
            required,
            isFuture,
          },
          send_time: {
            required,
            timeIsFuture: function (value) {
              return timeIsFuture(this.campaign.message.send_date, value)
            },
          },
        },
      },
    }
  },
}
</script>
